import type { CanvasElementType } from '@archer/shared';

/**
 * A single fill/stroke pass for a shape, expressed as a standard SVG path `d` string
 * in local shape coordinates (origin at the shape's top-left corner, spanning `w` x `h`).
 *
 * This is the single source of truth for shape geometry: the same `d` string is used to
 * paint the shape on the Konva canvas (via `Path2D`, see `ShapePath` in CanvasBoard.tsx),
 * to hit-test it, and to emit it verbatim into SVG/PNG export (see lib/export.ts). There is
 * no other place in the app that should know how a shape's outline is built.
 */
export interface ShapeLayer {
  d: string;
  /** 'base' = the shape's fill color, 'dark' = fill color darkened (for shading accents). */
  fill: 'base' | 'dark' | 'none';
  stroke: boolean;
}

export type ShapeDefinition = (w: number, h: number) => ShapeLayer[];

const rectDef: ShapeDefinition = (w, h) => [{ d: `M0,0 H${w} V${h} H0 Z`, fill: 'base', stroke: true }];

const ellipseDef: ShapeDefinition = (w, h) => {
  const rx = w / 2;
  const ry = h / 2;
  return [
    {
      d: `M0,${ry} A${rx},${ry} 0 0,1 ${w},${ry} A${rx},${ry} 0 0,1 0,${ry} Z`,
      fill: 'base',
      stroke: true,
    },
  ];
};

const stadiumDef: ShapeDefinition = (w, h) => {
  const r = Math.min(h / 2, w / 2);
  return [
    {
      d: `M${r},0 H${w - r} A${r},${r} 0 0,1 ${w - r},${h} H${r} A${r},${r} 0 0,1 ${r},0 Z`,
      fill: 'base',
      stroke: true,
    },
  ];
};

const diamondDef: ShapeDefinition = (w, h) => [
  { d: `M${w / 2},0 L${w},${h / 2} L${w / 2},${h} L0,${h / 2} Z`, fill: 'base', stroke: true },
];

const parallelogramDef: ShapeDefinition = (w, h) => {
  const o = Math.min(w * 0.18, 22);
  return [{ d: `M${o},0 L${w},0 L${w - o},${h} L0,${h} Z`, fill: 'base', stroke: true }];
};

const trapezoidDef: ShapeDefinition = (w, h) => {
  const o = Math.min(w * 0.18, 22);
  return [{ d: `M${o},0 L${w - o},0 L${w},${h} L0,${h} Z`, fill: 'base', stroke: true }];
};

const hexagonDef: ShapeDefinition = (w, h) => {
  const r = Math.min(w / 2, h / 2);
  const cx = w / 2;
  const cy = h / 2;
  const pts: string[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 2;
    pts.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
  }
  return [{ d: `M${pts.join(' L')} Z`, fill: 'base', stroke: true }];
};

/**
 * Cylinder ("database" shape), ported from Mermaid's own well-tested flowchart cylinder path
 * (mermaid/src/rendering-util/rendering-elements/shapes/cylinder.ts), adapted to our local
 * w/h box convention (cap radius `ry` insets the top/bottom instead of extending past `h`).
 *
 * The outline is a *single* open path: two same-direction arcs trace the full top ellipse,
 * then a side line, a third same-direction arc traces only the front (visible) half of the
 * bottom ellipse, then the other side line back up. Because there's no explicit `Z`, filling
 * auto-closes the path (giving the correct silhouette) while stroking does NOT draw that
 * implicit closing edge - so no stray line is drawn through the middle of the top cap, and the
 * hidden back half of the bottom ellipse is never drawn in the first place.
 */
const cylinderDef: ShapeDefinition = (w, h) => {
  const rx = w / 2;
  const ry = Math.min(h * 0.18, 18);
  const topY = ry;
  const bottomY = h - ry;
  const outline =
    `M0,${topY} ` +
    `A${rx},${ry} 0 0,0 ${w},${topY} ` +
    `A${rx},${ry} 0 0,0 0,${topY} ` +
    `L0,${bottomY} ` +
    `A${rx},${ry} 0 0,0 ${w},${bottomY} ` +
    `L${w},${topY}`;
  const cap = `M0,${topY} A${rx},${ry} 0 0,0 ${w},${topY} A${rx},${ry} 0 0,0 0,${topY} Z`;
  return [
    { d: outline, fill: 'base', stroke: true },
    { d: cap, fill: 'dark', stroke: false },
  ];
};

const documentDef: ShapeDefinition = (w, h) => {
  const wave = Math.min(h * 0.18, 14);
  return [
    {
      d:
        `M0,0 L${w},0 L${w},${h - wave} ` +
        `Q${w * 0.75},${h} ${w / 2},${h - wave / 2} ` +
        `Q${w * 0.25},${h - wave * 1.4} 0,${h - wave} Z`,
      fill: 'base',
      stroke: true,
    },
  ];
};

const noteDef: ShapeDefinition = (w, h) => {
  const fold = Math.min(w * 0.18, 18);
  return [
    { d: `M0,0 L${w - fold},0 L${w},${fold} L${w},${h} L0,${h} Z`, fill: 'base', stroke: true },
  ];
};

const personDef: ShapeDefinition = (w, h) => {
  const head = Math.min(w * 0.4, h * 0.4);
  const cx = w / 2;
  const r = head / 2;
  const headD = `M${cx - r},${head / 2} A${r},${r} 0 1,1 ${cx + r},${head / 2} A${r},${r} 0 1,1 ${cx - r},${head / 2} Z`;
  const bodyD =
    `M${cx - w / 2},${h} L${cx - w / 2 + 4},${head + head / 2} ` +
    `L${cx + w / 2 - 4},${head + head / 2} L${cx + w / 2},${h} Z`;
  return [
    { d: headD, fill: 'base', stroke: true },
    { d: bodyD, fill: 'base', stroke: true },
  ];
};

const queueDef: ShapeDefinition = (w, h) => {
  const r = Math.min(w / 4, h / 2, 12);
  return [
    {
      d:
        `M${r},0 H${w - r} A${r},${h / 2} 0 0,1 ${w - r},${h} H${r} ` +
        `A${r},${h / 2} 0 0,1 ${r},0 Z`,
      fill: 'base',
      stroke: true,
    },
  ];
};

const SHAPE_DEFINITIONS: Partial<Record<CanvasElementType, ShapeDefinition>> = {
  rect: rectDef,
  text: rectDef,
  stadium: stadiumDef,
  ellipse: ellipseDef,
  diamond: diamondDef,
  parallelogram: parallelogramDef,
  trapezoid: trapezoidDef,
  hexagon: hexagonDef,
  cylinder: cylinderDef,
  document: documentDef,
  note: noteDef,
  person: personDef,
  queue: queueDef,
};

/** Look up the layers for a shape type at a given size, falling back to a plain rectangle. */
export const getShapeLayers = (type: CanvasElementType | string, w: number, h: number): ShapeLayer[] =>
  (SHAPE_DEFINITIONS[type as CanvasElementType] ?? rectDef)(Math.max(w, 0), Math.max(h, 0));
